import { z } from 'zod';
import {
  PREVENTION_CONTROL_TYPES,
  type IpsControlSpec,
  type PreventionControlSpec,
} from './prevention.schema';

export const IPS_DIALECTS = {
  SURICATA_COMPATIBLE: 'suricataCompatible',
} as const;

export type IpsDialect = (typeof IPS_DIALECTS)[keyof typeof IPS_DIALECTS];

const SIGNATURE_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/;

const FINDING_CATEGORY_SIGNATURES = new Set([
  'SQL_INJECTION',
  // Bare XSS and WEAK_TLS are retired categories but stay rejected as generic labels.
  'XSS',
  'REFLECTED_XSS',
  'STORED_XSS',
  'DOM_XSS',
  'EXPOSED_SERVICE',
  'LOCAL_PRIVILEGE_ESCALATION',
  'WEAK_TLS',
  'INSECURE_TRANSPORT',
  'OTHER',
]);

const isFindingCategorySignature = (signatureId: string): boolean =>
  FINDING_CATEGORY_SIGNATURES.has(signatureId.toUpperCase());

export { isFindingCategorySignature };

export const IpsSignatureIdSchema = z
  .string()
  .trim()
  .min(1)
  .regex(
    SIGNATURE_ID_PATTERN,
    'signatureId must be a Suricata-compatible identifier (sid-NNNNNN or vendor.rule-name)',
  )
  .refine(
    (value) => !isFindingCategorySignature(value),
    'signatureId must not be the raw finding category',
  );

const SURICATA_DIALECT_CONSTRAINTS = [
  'signatureId must be a stable Suricata-compatible identifier (sid-NNNNNN or vendor.rule-name), not the raw finding category.',
  'Prefer action drop for confirmed exploitable findings; use alert only when blocking would disrupt legitimate traffic.',
  'Scope with targetIp or cidr when enriched_target includes host context.',
] as const;

export const buildIpsDialectConstraints = ({
  dialect = IPS_DIALECTS.SURICATA_COMPATIBLE,
}: {
  dialect?: IpsDialect;
} = {}): string[] => {
  if (dialect === IPS_DIALECTS.SURICATA_COMPATIBLE) {
    return [...SURICATA_DIALECT_CONSTRAINTS];
  }

  return [];
};

export const collectIpsDialectCapabilityViolations = ({
  spec,
}: {
  spec: PreventionControlSpec;
}): string[] => {
  if (spec.controlType !== PREVENTION_CONTROL_TYPES.IPS) {
    return [];
  }

  return collectIpsSpecCapabilityViolations(spec);
};

const collectIpsSpecCapabilityViolations = (spec: IpsControlSpec): string[] => {
  const violations: string[] = [];
  const signatureId = spec.signatureId.trim();

  if (!signatureId) {
    violations.push('IPS prevention requires a non-empty signatureId.');

    return violations;
  }

  if (!SIGNATURE_ID_PATTERN.test(signatureId)) {
    violations.push(
      `IPS signatureId "${signatureId}" is not a valid Suricata-compatible identifier.`,
    );
  }

  if (isFindingCategorySignature(signatureId)) {
    violations.push(
      'IPS signatureId must not be the raw finding category; derive a stable sid-* or vendor rule name.',
    );
  }

  return violations;
};

export const isIpsDialectCapabilityViolationError = (
  error: unknown,
): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return message.includes('ips') || message.includes('signatureid');
};
